import { useEffect } from "react";
import { siteMeta, locations } from "../../data/siteContent";

function upsertMeta(selector, attributes) {
    let element = document.head.querySelector(selector);

    if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
    }

    Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value);
    });
}

function upsertLink(rel, href) {
    let element = document.head.querySelector(`link[rel="${rel}"]`);

    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
    }

    element.setAttribute("href", href);
}

export default function Seo({
    title,
    description,
    path = "/",
    image = siteMeta.socialImage,
    type = "website",
    jsonLd,
}) {
    useEffect(() => {
        const canonicalUrl = `${siteMeta.siteUrl}${path}`;
        const fullTitle = `${title} | ${siteMeta.businessName}`;

        document.title = fullTitle;

        upsertMeta('meta[name="description"]', {
            name: "description",
            content: description,
        });
        upsertMeta('meta[name="robots"]', {
            name: "robots",
            content: "index, follow, max-image-preview:large",
        });
        upsertMeta('meta[property="og:title"]', {
            property: "og:title",
            content: fullTitle,
        });
        upsertMeta('meta[property="og:description"]', {
            property: "og:description",
            content: description,
        });
        upsertMeta('meta[property="og:type"]', {
            property: "og:type",
            content: type,
        });
        upsertMeta('meta[property="og:url"]', {
            property: "og:url",
            content: canonicalUrl,
        });
        upsertMeta('meta[property="og:image"]', {
            property: "og:image",
            content: `${siteMeta.siteUrl}${image}`,
        });
        upsertMeta('meta[name="twitter:card"]', {
            name: "twitter:card",
            content: "summary_large_image",
        });
        upsertMeta('meta[name="twitter:title"]', {
            name: "twitter:title",
            content: fullTitle,
        });
        upsertMeta('meta[name="twitter:description"]', {
            name: "twitter:description",
            content: description,
        });
        upsertMeta('meta[name="twitter:image"]', {
            name: "twitter:image",
            content: `${siteMeta.siteUrl}${image}`,
        });
        upsertLink("canonical", canonicalUrl);

        let script = document.getElementById("structured-data");
        if (!script) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.id = "structured-data";
            document.head.appendChild(script);
        }

        const baseJsonLd = {
            "@context": "https://schema.org",
            "@type": ["LocalBusiness", "MedicalBusiness", "ProfessionalService"],
            "@id": `${siteMeta.siteUrl}/#business`,
            name: siteMeta.businessName,
            alternateName: "Diëtistenpraktijk Vandaag Begin Ik",
            description: siteMeta.description,
            url: siteMeta.siteUrl,
            image: `${siteMeta.siteUrl}${siteMeta.socialImage}`,
            telephone: siteMeta.phone,
            email: siteMeta.email,
            priceRange: "$$",
            areaServed: {
                "@type": "City",
                name: "Breda",
                addressCountry: "NL",
            },
            knowsAbout: [
                "Diëtist Breda",
                "Dietist Breda",
                "Voedingsadvies Breda",
                "Dieetadvies Breda",
                "Leefstijlbegeleiding Breda",
                "Voeding",
                "Beweging",
                "Ontspanning",
            ],
            employee: {
                "@type": "Person",
                name: siteMeta.practitioner,
                jobTitle: "Diëtist en leefstijlcoach",
            },
            address: locations.map((location) => ({
                "@type": "PostalAddress",
                streetAddress: location.street,
                addressLocality: location.city,
                addressCountry: "NL",
            })),
            makesOffer: [
                {
                    "@type": "Offer",
                    itemOffered: {
                        "@type": "Service",
                        name: "Voedingsadvies",
                        areaServed: "Breda",
                    },
                },
                {
                    "@type": "Offer",
                    itemOffered: {
                        "@type": "Service",
                        name: "Dieetadvies",
                        areaServed: "Breda",
                    },
                },
                {
                    "@type": "Offer",
                    itemOffered: {
                        "@type": "Service",
                        name: "Leefstijlbegeleiding",
                        areaServed: "Breda",
                    },
                },
            ],
        };

        script.textContent = JSON.stringify(jsonLd || baseJsonLd);
    }, [description, image, jsonLd, path, title, type]);

    return null;
}
